import { useState } from 'react';

function validateRow(row) {
    const errors = [];
    if (!String(row.address ?? '').trim()) errors.push('Address is required');
    if (row.maxSpots != null && row.maxSpots !== '') {
        const n = parseInt(row.maxSpots, 10);
        if (!Number.isInteger(n) || n <= 0) errors.push('Spots must be a positive integer');
    }
    if (row.hourlyRate == null || row.hourlyRate === '') {
        errors.push('Hourly rate is required');
    } else {
        const n = parseFloat(row.hourlyRate);
        if (!Number.isFinite(n) || n <= 0) errors.push('Hourly rate must be a positive number');
    }
    return errors;
}

function RowEditor({ row, onSave, onCancel }) {
    const [draft, setDraft] = useState({
        address: row.address || '',
        maxSpots: row.maxSpots ?? '',
        hourlyRate: row.hourlyRate ?? '',
    });
    const [localError, setLocalError] = useState('');

    const handleSave = () => {
        const parsed = {
            ...row,
            address: draft.address.trim(),
            maxSpots: draft.maxSpots === '' ? null : parseInt(draft.maxSpots, 10),
            hourlyRate: draft.hourlyRate === '' ? null : parseFloat(draft.hourlyRate),
        };
        const errors = validateRow(parsed);
        if (errors.length) {
            setLocalError(errors.join('; '));
            return;
        }
        onSave(parsed);
    };

    return (
        <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%' }}>
                <span className="row-num">Row {row.rowIndex}</span>
                <span className="row-msg">
                    {row.error} — edit the fields below and click Save to re-validate.
                </span>
            </div>
            <div className="validation-edit-fields">
                <input
                    type="text"
                    value={draft.address}
                    onChange={(e) => setDraft((d) => ({ ...d, address: e.target.value }))}
                    placeholder="Address"
                    aria-label="Address"
                />
                <input
                    type="number"
                    value={draft.maxSpots}
                    onChange={(e) => setDraft((d) => ({ ...d, maxSpots: e.target.value }))}
                    placeholder="Spots"
                    aria-label="Spots"
                    min="1"
                />
                <input
                    type="number"
                    value={draft.hourlyRate}
                    onChange={(e) => setDraft((d) => ({ ...d, hourlyRate: e.target.value }))}
                    placeholder="Hourly rate"
                    aria-label="Hourly rate"
                    min="0.01"
                    step="0.25"
                />
            </div>
            {localError && (
                <div className="bulk-alert-error" role="alert">{localError}</div>
            )}
            <div className="validation-edit-actions">
                <button type="button" className="btn btn-secondary" onClick={onCancel}>Cancel</button>
                <button type="button" className="btn btn-primary" onClick={handleSave}>Save & re-validate</button>
            </div>
        </>
    );
}

export default function Step2Errors({
    valid,
    invalid,
    droppedLines,
    onUpdate,
    onBack,
    onContinue,
}) {
    const [editingIndex, setEditingIndex] = useState(null);

    const handleSaveRow = (index, fixed) => {
        const nextInvalid = invalid.filter((_, i) => i !== index);
        onUpdate([...valid, fixed], nextInvalid);
        setEditingIndex(null);
    };

    const canContinue = valid.length > 0;
    const validCount = valid.length;

    const heading = invalid.length === 0
        ? `All rows valid · ${validCount} ready to enrich`
        : `${invalid.length} row${invalid.length === 1 ? '' : 's'} have issues`;

    const subCopy = invalid.length === 0
        ? `${validCount} valid row${validCount === 1 ? '' : 's'} ready to continue.`
        : `Fix each row inline (click "Edit row"), or skip to continue with the ${validCount} valid row${validCount === 1 ? '' : 's'}.`;

    return (
        <div className="bulk-state">
            <div className="bulk-state-title">Step 2 of 4 · Validation</div>
            <h3>{heading}</h3>
            <p className="bulk-state-sub">{subCopy}</p>

            {droppedLines?.length > 0 && (
                <p className="bulk-state-sub">
                    {droppedLines.length} line{droppedLines.length !== 1 ? 's' : ''} skipped during extraction
                    ({droppedLines.map((d) => d.reason).slice(0, 3).join(', ')}…)
                </p>
            )}

            {invalid.length > 0 && (
                <div className="validation-list">
                    {invalid.map((row, index) => (
                        editingIndex === index ? (
                            <div className="validation-row editing" key={row.rowIndex ?? index}>
                                <RowEditor
                                    row={row}
                                    onSave={(fixed) => handleSaveRow(index, fixed)}
                                    onCancel={() => setEditingIndex(null)}
                                />
                            </div>
                        ) : (
                            <div className="validation-row" key={row.rowIndex ?? index}>
                                <span className="row-num">Row {row.rowIndex ?? index + 1}</span>
                                <span className="row-msg">
                                    {row.error} · &quot;{row.address || ''}&quot;,{row.maxSpots ?? ''},{row.hourlyRate ?? ''}
                                </span>
                                <button
                                    type="button"
                                    className="row-edit-btn"
                                    onClick={() => setEditingIndex(index)}
                                >
                                    Edit row
                                </button>
                            </div>
                        )
                    ))}
                </div>
            )}

            <div className="bulk-actions split">
                <button type="button" className="btn btn-secondary" onClick={onBack}>
                    ← Back to paste
                </button>
                <div style={{ display: 'flex', gap: 10 }}>
                    {invalid.length > 0 ? (
                        <button
                            type="button"
                            className="btn btn-secondary"
                            disabled={!canContinue}
                            title={!canContinue ? 'Fix at least one row or go back to paste' : undefined}
                            onClick={onContinue}
                        >
                            Skip errors, continue with {validCount} valid →
                        </button>
                    ) : (
                        <button
                            type="button"
                            className="btn btn-primary"
                            disabled={!canContinue}
                            onClick={onContinue}
                        >
                            Continue with all {validCount} valid →
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}
